import { Component, Input, Output, EventEmitter, OnInit, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material.module';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import { PyramidDto } from '../../../models/api/pyramidDto';
import { MatCardModule } from '@angular/material/card';
import { Helpers } from '../../../utilities/helpers';

export interface DeletePyramidYearEvent {
  pyramidId: string;
  year: number;
}

@Component({
  selector: 'app-pyramids-year-card',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    BaseChartDirective,
    MatCardModule
  ],
  templateUrl: './pyramids-year-card.component.html',
  styleUrl: './pyramids-year-card.component.scss'
})
export class PyramidsYearCardComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective<'pie'> | undefined;
  @Input() year!: number;
  @Input() pyramids: PyramidDto[] = [];
  @Input() showTextView = false;
  @Output() deletePyramidYear = new EventEmitter<DeletePyramidYearEvent>();

  public pieChartType = 'pie' as const;
  public pieChartData!: ChartData<'pie'>;
  public pieChartOptions!: ChartConfiguration<'pie'>['options'];

  ngOnInit() {
    this.initializeChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reinitialize chart when pyramids data changes
    if (changes['pyramids'] && !changes['pyramids'].firstChange) {
      this.initializeChart();
      // Update the chart if it exists
      setTimeout(() => {
        this.chart?.update();
      }, 0);
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.chart?.update();
    }, 0);
  }

  private initializeChart() {
    if (!this.pyramids || this.pyramids.length === 0) {
      return;
    }

    const styles = getComputedStyle(document.body);
    const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

    const parseColor = (cssValue: string, fallback: string): string => {
      if (!cssValue) return fallback;

      const lightDarkMatch = cssValue.match(/light-dark\(([^,]+),\s*([^)]+)\)/);
      if (lightDarkMatch) {
        return isDarkMode ? lightDarkMatch[2].trim() : lightDarkMatch[1].trim();
      }

      return cssValue;
    };

    const secondaryColor = parseColor(styles.getPropertyValue('--mat-sys-secondary').trim(), '#6750A4');
    const cardBackgroundColor = parseColor(styles.getPropertyValue('--mat-sys-surface-container-lowest').trim(), '#F5F5F5');
    const onSurfaceColor = parseColor(styles.getPropertyValue('--mat-sys-on-surface').trim(), '#1C1B1F');
    const surfaceDimColor = parseColor(styles.getPropertyValue('--mat-sys-surface-dim').trim(), '#E0E0E0');
    const onPrimaryFixedColor = parseColor(styles.getPropertyValue('mat-sys-on-primary-fixed').trim(), '#000000');

    // Generate gradient color palette based on secondary color
    const colorPalette = Helpers.generateGradientPalette(secondaryColor, this.pyramids.length);

    // Extract labels (entity names) and data (percentages) from pyramids
    const labels = this.pyramids.map(p => this.getPyramidName(p));
    const data = this.pyramids.map(p => p.percentage);

    this.pieChartData = {
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: colorPalette,
          borderColor: cardBackgroundColor,
          borderWidth: 1,
          hoverBorderColor: cardBackgroundColor,
          hoverBorderWidth: 1,
        }
      ]
    };

    this.pieChartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2,
      plugins: {
        legend: {
          display: true,
          position: 'right',
          labels: {
            color: onPrimaryFixedColor,
            font: {
              family: 'Roboto, sans-serif',
              size: 12
            },
            padding: 10,
            boxWidth: 15,
            boxHeight: 15
          }
        },
        datalabels: {
          display: false
        },
        tooltip: {
          backgroundColor: cardBackgroundColor,
          titleColor: onSurfaceColor,
          bodyColor: onSurfaceColor,
          borderColor: surfaceDimColor,
          borderWidth: 1,
          callbacks: {
            label: (context) => {
              const label = context.label || '';
              const value = context.parsed || 0;
              return `${label}: ${value.toFixed(2)}%`;
            }
          }
        }
      }
    };
  }

  getPyramidName(pyramid: PyramidDto): string {
    return pyramid.pyramidLargeShareholderName
      || pyramid.pyramidalShareholderName
      || (pyramid as any).pyramidPyramidalShareholderName  // For PyramidalShareholderPyramidDto
      || (pyramid as any).largeShareholderName  // For PyramidalShareholderPyramidDto
      || pyramid.companyName
      || pyramid.opbCompanyName
      || pyramid.directorName
      || 'Unknown';
  }

  get sortedPyramids() {
    if (!this.pyramids) {
      return [];
    }
    return [...this.pyramids].sort((a, b) => b.percentage - a.percentage);
  }

  onDeletePyramid(pyramidId: string, event: Event) {
    event.stopPropagation();
    this.deletePyramidYear.emit({ pyramidId, year: this.year });
  }
}
